import React, { useState, useMemo } from 'react';
import { FilterConfig } from '../../../types';
import { exportTableData, ExportOptions } from '../../../utils/exportUtils';
import { LazyList } from '../LazyList';

export interface TableColumn<T> {
  key: keyof T;
  title: string;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface PaginationConfig {
  page: number;
  limit: number;
  total: number;
  showSizeChanger?: boolean;
  pageSizeOptions?: number[];
}

export interface DataTableProps<T> {
  data: T[];
  columns: TableColumn<T>[];
  filters?: FilterConfig[];
  pagination?: PaginationConfig;
  loading?: boolean;
  onRowSelect?: (row: T) => void;
  onSort?: (field: keyof T, order: 'asc' | 'desc') => void;
  onFilter?: (filters: FilterConfig[]) => void;
  onPageChange?: (page: number, limit: number) => void;
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
  bordered?: boolean;
  compact?: boolean;
  emptyMessage?: string;
  // Export functionality
  enableExport?: boolean;
  exportFilename?: string;
  showExportButtons?: boolean;
  exportFormats?: Array<'csv' | 'json'>;
  // Lazy loading
  enableLazyLoading?: boolean;
  rowHeight?: number;
  containerHeight?: number;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  filters = [],
  pagination,
  loading = false,
  onRowSelect,
  onSort,
  onFilter,
  onPageChange,
  className = '',
  striped = true,
  hoverable = true,
  bordered = true,
  compact = false,
  emptyMessage = 'No data available',
  // Export functionality
  enableExport = false,
  exportFilename = 'table-data',
  showExportButtons = false,
  exportFormats = ['csv', 'json'],
  // Lazy loading
  enableLazyLoading = false,
  rowHeight = 50,
  containerHeight = 400,
  onLoadMore,
  hasMore = false
}: DataTableProps<T>) {
  const [sortConfig, setSortConfig] = useState<{ key: keyof T; direction: 'asc' | 'desc' } | null>(null);

  const handleSort = (key: keyof T) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    onSort?.(key, direction);
  };

  const getSortIcon = (key: keyof T) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <span className="text-neutral-400">&#8597;</span>;
    }
    return sortConfig.direction === 'asc' ? <span>&#8593;</span> : <span>&#8595;</span>;
  };

  // Base container classes
  const containerBaseClasses = 'w-full bg-white rounded-xl shadow-sm overflow-hidden dark:bg-neutral-850';
  const borderedClasses = bordered ? 'border border-neutral-200 dark:border-neutral-700' : '';
  const tableContainerClasses = `${containerBaseClasses} ${borderedClasses} ${className}`.trim();

  const paginatedData = useMemo(() => {
    if (!pagination) return data;
    const start = (pagination.page - 1) * pagination.limit;
    const end = start + pagination.limit;
    return data.slice(start, end);
  }, [data, pagination]);

  // Export handlers
  const handleExport = (format: 'csv' | 'json') => {
    const exportOptions: ExportOptions = {
      filename: exportFilename,
      format,
      includeHeaders: true
    };
    
    const exportColumns = columns.map(col => ({
      key: String(col.key),
      title: col.title,
      render: col.render
    }));
    
    exportTableData(data, exportColumns, exportOptions);
  };

  // Padding classes based on compact mode
  const cellPadding = compact ? 'px-3 py-2' : 'px-4 py-3';
  const headerPadding = compact ? 'px-3 py-2' : 'px-4 py-3';

  // Export button classes
  const exportButtonClasses = `
    px-3 py-1.5 min-h-touch
    bg-primary-600 hover:bg-primary-700
    text-white text-xs font-medium
    border-0 rounded-md
    cursor-pointer
    transition-colors duration-200
    whitespace-nowrap
    dark:bg-primary-500 dark:hover:bg-primary-600
    md:px-3 md:py-1.5
    sm:text-[11px] sm:px-2 sm:py-1
  `.trim().replace(/\s+/g, ' ');

  if (loading) {
    return (
      <div className={tableContainerClasses}>
        <div className="flex items-center justify-center gap-3 p-10">
          <div className="w-5 h-5 border-2 border-neutral-200 border-t-primary-500 rounded-full animate-spin dark:border-neutral-700 dark:border-t-primary-400"></div>
          <span className="text-neutral-600 dark:text-neutral-400">Loading...</span>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={tableContainerClasses}>
        {(enableExport || showExportButtons) && (
          <div className="flex justify-end p-3 md:p-4 border-b border-neutral-200 bg-neutral-50 dark:bg-neutral-800 dark:border-neutral-700 flex-col md:flex-row gap-3 md:gap-0">
            <div className="flex gap-2 flex-col sm:flex-row sm:grid sm:grid-cols-2 md:flex md:flex-row">
              {exportFormats.includes('csv') && (
                <button
                  onClick={() => handleExport('csv')}
                  className={exportButtonClasses}
                  title="Export as CSV"
                >
                  Export CSV
                </button>
              )}
              {exportFormats.includes('json') && (
                <button
                  onClick={() => handleExport('json')}
                  className={exportButtonClasses}
                  title="Export as JSON"
                >
                  Export JSON
                </button>
              )}
            </div>
          </div>
        )}
        <div className="text-center p-10 text-neutral-500 dark:text-neutral-400">
          <p>{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Helper function for row classes
  const getRowClasses = (index: number) => {
    const baseClasses = 'transition-colors duration-200';
    const stripedClasses = striped ? 'even:bg-neutral-50 dark:even:bg-neutral-800/50' : '';
    const hoverableClasses = hoverable ? 'hover:bg-primary-50 dark:hover:bg-primary-900/20' : '';
    const clickableClasses = onRowSelect ? 'cursor-pointer focus:outline-2 focus:outline-primary-500 focus:-outline-offset-2' : '';
    return `${baseClasses} ${stripedClasses} ${hoverableClasses} ${clickableClasses}`.trim();
  };

  // Pagination button classes
  const paginationButtonClasses = `
    px-3 py-1.5 min-w-touch min-h-touch
    border border-neutral-300
    bg-white hover:bg-neutral-100 hover:border-neutral-400
    cursor-pointer rounded-md
    text-sm
    transition-all duration-200
    disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-neutral-300
    dark:bg-neutral-800 dark:border-neutral-600 dark:hover:bg-neutral-700 dark:hover:border-neutral-500
    dark:disabled:hover:bg-neutral-800 dark:disabled:hover:border-neutral-600
  `.trim().replace(/\s+/g, ' ');

  // Header cell base classes
  const thBaseClasses = `
    ${headerPadding}
    bg-neutral-50 text-left font-semibold text-neutral-700
    border-b-2 border-neutral-200 whitespace-nowrap
    dark:bg-neutral-800 dark:text-neutral-200 dark:border-neutral-700
    lg:${headerPadding} md:px-3 md:py-2.5 md:text-[13px] sm:px-2 sm:py-2 sm:text-xs
  `.trim().replace(/\s+/g, ' ');

  // Table cell base classes
  const tdBaseClasses = `
    ${cellPadding} align-middle
    border-b border-neutral-200
    dark:border-neutral-700
    lg:${cellPadding} md:px-3 md:py-2.5 md:text-[13px] sm:px-1.5 sm:py-2 sm:text-xs
  `.trim().replace(/\s+/g, ' ');

  return (
    <div className={tableContainerClasses}>
      {/* Export buttons header */}
      {(enableExport || showExportButtons) && (
        <div className="flex justify-end p-3 md:p-4 border-b border-neutral-200 bg-neutral-50 dark:bg-neutral-800 dark:border-neutral-700 flex-col lg:flex-row gap-3 lg:gap-0">
          <div className="flex gap-2 flex-col sm:flex-row sm:grid sm:grid-cols-2 lg:flex lg:flex-row">
            {exportFormats.includes('csv') && (
              <button
                onClick={() => handleExport('csv')}
                className={exportButtonClasses}
                title="Export as CSV"
              >
                Export CSV
              </button>
            )}
            {exportFormats.includes('json') && (
              <button
                onClick={() => handleExport('json')}
                className={exportButtonClasses}
                title="Export as JSON"
              >
                Export JSON
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table container with horizontal scroll on mobile */}
      <div className="
        relative
        overflow-x-auto
        scrollbar-thin scrollbar-thumb-neutral-300 dark:scrollbar-thumb-neutral-600
        -webkit-overflow-scrolling-touch
      ">
        {/* Scroll hint gradient for mobile */}
        <div className="
          absolute right-0 top-0 bottom-0 w-8
          bg-gradient-to-l from-white dark:from-neutral-850 to-transparent
          pointer-events-none
          md:hidden
          z-10
        " />
        <table className="w-full border-collapse text-sm min-w-[640px] lg:min-w-0">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  style={{
                    width: column.width,
                    textAlign: column.align || 'left'
                  }}
                  className={`${thBaseClasses} ${column.sortable ? 'cursor-pointer select-none hover:bg-neutral-100 dark:hover:bg-neutral-700' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span>{column.title}</span>
                    {column.sortable && (
                      <button
                        onClick={() => handleSort(column.key)}
                        className="
                          bg-transparent border-0 cursor-pointer
                          p-0.5 rounded
                          text-neutral-500 text-xs
                          hover:bg-black/10
                          min-w-touch min-h-touch
                          flex items-center justify-center
                          dark:text-neutral-400 dark:hover:bg-white/10
                        "
                        aria-label={`Sort by ${column.title}`}
                      >
                        {getSortIcon(column.key)}
                      </button>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          {!enableLazyLoading && (
            <tbody>
              {paginatedData.map((row, index) => (
                <tr
                  key={index}
                  onClick={() => onRowSelect?.(row)}
                  className={getRowClasses(index)}
                  role={onRowSelect ? 'button' : undefined}
                  tabIndex={onRowSelect ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (onRowSelect && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowSelect(row);
                    }
                  }}
                >
                  {columns.map((column) => (
                    <td
                      key={String(column.key)}
                      className={tdBaseClasses}
                      style={{ textAlign: column.align || 'left' }}
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : String(row[column.key] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Lazy loading container */}
      {enableLazyLoading && (
        <div
          className="border border-neutral-200 border-t-0 overflow-hidden dark:border-neutral-700"
          style={{ height: containerHeight }}
        >
          <LazyList
            items={paginatedData}
            itemHeight={rowHeight}
            containerHeight={containerHeight}
            loading={loading}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            renderItem={(row, index) => (
              <div
                className={`
                  flex items-center py-3 px-3
                  border-b border-neutral-200
                  transition-colors duration-150
                  hover:bg-black/[0.075]
                  dark:border-neutral-700 dark:hover:bg-white/[0.075]
                  ${onRowSelect ? 'cursor-pointer focus:outline-2 focus:outline-primary-500 focus:-outline-offset-2' : ''}
                `}
                onClick={() => onRowSelect?.(row)}
                role={onRowSelect ? 'button' : undefined}
                tabIndex={onRowSelect ? 0 : undefined}
                onKeyDown={(e) => {
                  if (onRowSelect && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onRowSelect(row);
                  }
                }}
              >
                {columns.map((column, colIndex) => (
                  <div
                    key={String(column.key)}
                    className={`
                      overflow-hidden text-ellipsis whitespace-nowrap
                      ${colIndex === 0 ? 'pl-0' : 'px-3'}
                      ${colIndex === columns.length - 1 ? 'pr-0' : ''}
                    `}
                    style={{
                      textAlign: column.align || 'left',
                      width: column.width || 'auto',
                      flex: column.width ? 'none' : '1'
                    }}
                  >
                    {column.render
                      ? column.render(row[column.key], row)
                      : String(row[column.key] ?? '')}
                  </div>
                ))}
              </div>
            )}
            emptyComponent={
              <div className="text-center p-10 text-neutral-500 dark:text-neutral-400">
                {emptyMessage}
              </div>
            }
          />
        </div>
      )}

      {/* Pagination */}
      {pagination && (
        <div className="
          flex items-center justify-between
          p-4 gap-4 flex-wrap
          bg-neutral-50 border-t border-neutral-200
          dark:bg-neutral-800 dark:border-neutral-700
          lg:flex-row
          md:flex-col md:items-stretch md:gap-3
        ">
          {/* Pagination info */}
          <div className="text-neutral-500 text-sm dark:text-neutral-400 md:text-center">
            Showing {Math.min((pagination.page - 1) * pagination.limit + 1, pagination.total)} to{' '}
            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
          </div>

          {/* Pagination controls */}
          <div className="flex items-center gap-2 md:justify-center">
            <button
              disabled={pagination.page === 1}
              onClick={() => onPageChange?.(1, pagination.limit)}
              className={paginationButtonClasses}
              aria-label="First page"
            >
              <span className="sr-only sm:not-sr-only">First</span>
              <span className="sm:hidden" aria-hidden="true">&#x23EE;</span>
            </button>
            <button
              disabled={pagination.page === 1}
              onClick={() => onPageChange?.(pagination.page - 1, pagination.limit)}
              className={paginationButtonClasses}
              aria-label="Previous page"
            >
              <span className="sr-only sm:not-sr-only">Prev</span>
              <span className="sm:hidden" aria-hidden="true">&#x23EA;</span>
            </button>

            <span className="mx-3 font-medium text-neutral-700 dark:text-neutral-300">
              Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}
            </span>

            <button
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              onClick={() => onPageChange?.(pagination.page + 1, pagination.limit)}
              className={paginationButtonClasses}
              aria-label="Next page"
            >
              <span className="sr-only sm:not-sr-only">Next</span>
              <span className="sm:hidden" aria-hidden="true">&#x23E9;</span>
            </button>
            <button
              disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              onClick={() => onPageChange?.(Math.ceil(pagination.total / pagination.limit), pagination.limit)}
              className={paginationButtonClasses}
              aria-label="Last page"
            >
              <span className="sr-only sm:not-sr-only">Last</span>
              <span className="sm:hidden" aria-hidden="true">&#x23ED;</span>
            </button>
          </div>

          {/* Page size selector */}
          {pagination.showSizeChanger && pagination.pageSizeOptions && (
            <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400 md:justify-center">
              <label htmlFor="page-size">Show:</label>
              <select
                id="page-size"
                value={pagination.limit}
                onChange={(e) => onPageChange?.(1, parseInt(e.target.value))}
                className="
                  px-2 py-1 min-h-touch
                  border border-neutral-300 rounded-md
                  bg-white
                  text-sm
                  dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-300
                "
              >
                {pagination.pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
              <span>per page</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}